import React, { useState } from "react";
import { submitWaitlist } from "../utils/waitlistApi";
import { stateName } from "../utils/usStates";
import { errorMessage } from "../utils/errorMessage";
import { useSubmitGuard } from "../utils/useSubmitGuard";
import "../styles/StateGate.css";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP_PATTERN = /^\d{5}$/;

/*
 * Shown instead of signup to someone outside the states Youro serves.
 *
 * It collects only what it says it does: the state, an optional zip code, and an
 * email address only if the person ticks the box asking to be emailed. The box
 * starts unticked, like every consent box in this app. No name, no symptoms —
 * this is interest in Youro becoming available, not the start of a patient
 * record.
 */
const WaitlistForm = ({ stateCode, onChangeState }) => {
  const name = stateName(stateCode);
  const [wantsNotification, setWantsNotification] = useState(false);
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);
  const [submitting, guard] = useSubmitGuard();

  const validate = () => {
    const next = {};
    if (wantsNotification && !EMAIL_PATTERN.test(email.trim())) {
      next.email = "Enter your email address so we can tell you when Youro is available.";
    }
    if (zip.trim() && !ZIP_PATTERN.test(zip.trim())) {
      next.zip = "Enter a 5-digit zip code, or leave it blank.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = guard(async (e) => {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) return;
    try {
      await submitWaitlist({ state: stateCode, zip, email, wantsNotification });
      setDone(true);
    } catch (err) {
      setSubmitError(errorMessage(err, "We couldn't save that just now. Please try again in a moment."));
    }
  });

  if (done) {
    return (
      <div className="gate-panel" role="status">
        <h2 className="gate-heading">You're on the list — thanks for your interest</h2>
        {wantsNotification ? (
          <p className="gate-text">We'll email {email.trim()} if Youro becomes available in {name}.</p>
        ) : (
          <p className="gate-text">Thanks for letting us know where you are.</p>
        )}
      </div>
    );
  }

  return (
    <form className="gate-panel" onSubmit={onSubmit} noValidate>
      <h2 className="gate-heading">Youro isn't available in {name} yet</h2>
      <p className="gate-text">
        Right now, South Carolina licensure means we can only treat patients physically located in South
        Carolina. We're tracking interest as we think about where to expand next.
      </p>

      <label className="gate-check">
        <input
          type="checkbox"
          checked={wantsNotification}
          onChange={(e) => {
            setWantsNotification(e.target.checked);
            setErrors((prev) => ({ ...prev, email: undefined }));
          }}
        />
        <span>Email me if Youro becomes available in {name}</span>
      </label>

      {wantsNotification && (
        <div className="gate-field">
          <label htmlFor="waitlist-email">Email</label>
          <input
            id="waitlist-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={errors.email ? "waitlist-email-error" : undefined}
            onChange={(e) => setEmail(e.target.value)}
          />
          {errors.email && <span id="waitlist-email-error" className="gate-error">{errors.email}</span>}
        </div>
      )}

      <div className="gate-field">
        <label htmlFor="waitlist-zip">
          Zip code <span className="gate-optional">(optional)</span>
        </label>
        <input
          id="waitlist-zip"
          inputMode="numeric"
          autoComplete="postal-code"
          maxLength={5}
          value={zip}
          aria-invalid={Boolean(errors.zip)}
          aria-describedby={errors.zip ? "waitlist-zip-error" : undefined}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
        />
        {errors.zip && <span id="waitlist-zip-error" className="gate-error">{errors.zip}</span>}
      </div>

      {submitError && <p className="gate-error" role="alert">{submitError}</p>}

      {/* Secondary, not Coral: the primary action is reserved for signing up in
          South Carolina. */}
      <button type="submit" className="gate-btn-secondary" disabled={submitting} aria-busy={submitting}>
        {submitting ? "Joining…" : "Join the waitlist"}
      </button>
      <p className="gate-fineprint">
        We'll only email you about Youro availability in your area. No spam, unsubscribe anytime.
      </p>

      <button type="button" className="gate-btn-ghost" onClick={onChangeState}>
        I live somewhere else
      </button>
    </form>
  );
};

export default WaitlistForm;
